import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { MessageSquare, RefreshCw } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import EmptyState from '../../../components/Common/SafeApp/EmptyState';
import configData from '../../../configure';

export const AdminStatsGrid = ({
  user,
  statsCards,
  loading,
  onNavigate,
  getFullName,
  formatPhoneNumber,
}) => (
  <div className="stats-grid">
    <div className="admin-profile-card">
      <div className="admin-profile-content">
        <div className="admin-profile-header">
          <div className="admin-profile-identity">
            <span className="admin-badge">Admin</span>
            <h2 className="admin-welcome-name">{getFullName() || 'Egurukul Admin'}</h2>
            <div className="admin-contact-info">
              <div className="admin-contact-item">
                <span className="contact-icon">✉️</span> {user?.email || 'admin@egurukulapps.com'}
              </div>
              <div className="admin-contact-item">
                <span className="contact-icon">📞</span> {formatPhoneNumber(user?.phoneNumber) || 'Not available'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {statsCards.map((card) => (
      <div
        key={card.title}
        className={`stat-card ${card.className} clickable`}
        onClick={() => onNavigate(card.route)}
      >
        <div className="stat-card-text">
          <p>{card.title}</p>
          <h3>{loading ? '...' : card.value}</h3>
        </div>
        <div className="stat-card-icon">
          <card.icon size={20} />
        </div>
      </div>
    ))}
  </div>
);

export const CustomOrgStatsGrid = ({ counts, loading, onNavigate }) => {
  const cards = [
    { title: 'TOTAL KVS', value: counts?.kvsCount ?? 0, subtitle: 'Classes running', className: 'card-kvs', route: '/classes' },
    { title: 'NIRVAHAKS', value: counts?.nirvahakCount ?? 0, subtitle: 'Faculty', className: 'card-nirvahaks', route: '/kv-faculty/nirvahak' },
    { title: 'PARIAVEKSHAKS', value: counts?.paryavekshakCount ?? 0, subtitle: 'Supervisors L1', className: 'card-pariavekshaks', route: '/kv-faculty/paryavekshak' },
    { title: 'SAMANVAYAKARTAS', value: counts?.samanvaykartaCount ?? 0, subtitle: 'Supervisors L2', className: 'card-samanvayakartas', route: '/kv-faculty/samanvaykarta' },
    { title: 'STUDENTS', value: counts?.studentCount ?? 0, subtitle: 'Enrolled', className: 'card-students', route: '/Students' },
  ];

  return (
    <div className="custom-org-stats-grid">
      {cards.map((card, index) => (
        <div 
          key={index} 
          className={`custom-stat-card ${card.className} clickable`}
          onClick={() => onNavigate && card.route ? onNavigate(card.route) : null}
          style={{ cursor: 'pointer' }}
        >
          <div className="custom-stat-title">{card.title}</div>
          <div className="custom-stat-value">
            {loading ? (
              <span className="custom-stat-value-loading-shimmer" />
            ) : (
              card.value
            )}
          </div>
          <div className="custom-stat-subtitle">{card.subtitle}</div>
        </div>
      ))}
    </div>
  );
};

const FEE_MONTH_OPTIONS = [
  { value: 'ALL', label: 'All Months' },
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
];

const parsePaymentMonthIndex = (dateVal) => {
  if (!dateVal) return null;
  if (Array.isArray(dateVal)) {
    const m = Number(dateVal[1]);
    return !isNaN(m) && m >= 1 && m <= 12 ? m - 1 : null;
  }
  if (typeof dateVal === 'object' && !(dateVal instanceof Date)) {
    const m = dateVal.monthValue || dateVal.month;
    if (typeof m === 'number') return m >= 1 && m <= 12 ? m - 1 : null;
    if (typeof m === 'string') {
      const months = {
        JANUARY: 0, FEBRUARY: 1, MARCH: 2, APRIL: 3, MAY: 4, JUNE: 5,
        JULY: 6, AUGUST: 7, SEPTEMBER: 8, OCTOBER: 9, NOVEMBER: 10, DECEMBER: 11
      };
      if (months[m.toUpperCase()] !== undefined) return months[m.toUpperCase()];
    }
  }
  if (typeof dateVal === 'number') {
    const ts = dateVal > 1e11 ? dateVal : dateVal * 1000;
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d.getMonth();
  }
  const d = new Date(dateVal);
  if (!isNaN(d.getTime())) {
    return d.getMonth();
  }
  return null;
};

export const AdminFeeSummaryCard = ({ feeSummary, formatCurrency, onViewReports }) => {
  const [selectedMonth, setSelectedMonth] = React.useState('ALL');

  const displayedStats = React.useMemo(() => {
    if (selectedMonth === 'ALL') {
      return {
        collected: feeSummary?.collected || 0,
        pending: feeSummary?.pending || 0,
        rate: feeSummary?.rate || '0.0',
        paymentCount: Array.isArray(feeSummary?.payments) ? feeSummary.payments.length : 0,
      };
    }

    const monthIdx = Number(selectedMonth);
    const payments = Array.isArray(feeSummary?.payments) ? feeSummary.payments : [];

    const monthlyPayments = payments.filter((p) => {
      const rawDate = p.paymentDate || p.createdAt || p.paymentTimestamp || p.date || p.installmentDueDate;
      const m = parsePaymentMonthIndex(rawDate);
      return m === monthIdx;
    });

    const monthlyCollected = monthlyPayments.reduce(
      (sum, p) => sum + Number(p.amountPaid || p.amount || 0),
      0
    );

    // Check if installments are due in this month
    let scheduledExpected = 0;
    if (feeSummary?.installmentMap && feeSummary?.effectiveStudentCount) {
      Object.values(feeSummary.installmentMap).forEach((inst) => {
        if (inst.dueDate) {
          const dueMonth = parsePaymentMonthIndex(inst.dueDate);
          if (dueMonth === monthIdx) {
            scheduledExpected += (Number(inst.amount) || 0) * feeSummary.effectiveStudentCount;
          }
        }
      });
    }

    const totalExp =
      Number(feeSummary?.totalExpected || 0) ||
      ((feeSummary?.collected || 0) + (feeSummary?.pending || 0));
    const defaultMonthlyTarget = Math.round(totalExp / 12);
    const monthlyExpected =
      scheduledExpected > 0
        ? Math.max(monthlyCollected, scheduledExpected)
        : defaultMonthlyTarget > 0
        ? Math.max(monthlyCollected, defaultMonthlyTarget)
        : monthlyCollected;

    const monthlyPending = Math.max(0, monthlyExpected - monthlyCollected);
    const monthlyRate =
      monthlyExpected > 0
        ? Math.min(100, (monthlyCollected / monthlyExpected) * 100).toFixed(1)
        : monthlyCollected > 0
        ? '100.0'
        : '0.0';

    return {
      collected: monthlyCollected,
      pending: monthlyPending,
      rate: monthlyRate,
      paymentCount: monthlyPayments.length,
    };
  }, [selectedMonth, feeSummary]);

  const activeMonthOption = FEE_MONTH_OPTIONS.find((m) => m.value === selectedMonth);
  const monthLabel = activeMonthOption?.label || 'Selected Month';

  return (
    <div className="dashboard-card fee-summary-panel">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 className="card-title">Fee Summary</h3>
          <span className="card-subtitle">
            {selectedMonth === 'ALL'
              ? 'Summary Report'
              : `${monthLabel} (${displayedStats.paymentCount} ${displayedStats.paymentCount === 1 ? 'payment' : 'payments'})`}
          </span>
        </div>
        <div className="fee-header-controls">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            disabled={Boolean(feeSummary?.loading)}
            aria-label="Filter fee summary by month"
            className="fee-month-select"
          >
            {FEE_MONTH_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {feeSummary?.loading ? (
        <p style={{ opacity: 0.5, fontSize: '0.85rem', padding: '0.5rem 0' }}>Loading fee data...</p>
      ) : feeSummary?.collected === 0 && feeSummary?.pending === 0 && selectedMonth === 'ALL' ? (
        <p style={{ opacity: 0.5, fontSize: '0.85rem', padding: '0.5rem 0' }}>No fee data available yet.</p>
      ) : (
        <div>
          <div className="fee-summary-item">
            <span className="fee-label">
              {selectedMonth === 'ALL' ? 'Total Collected' : `${monthLabel} Collected`}
            </span>
            <span className="fee-amount collected">{formatCurrency(displayedStats.collected)}</span>
          </div>
          <div className="fee-summary-item">
            <span className="fee-label">
              {selectedMonth === 'ALL' ? 'Pending / Unpaid' : `${monthLabel} Pending`}
            </span>
            <span className="fee-amount pending">{formatCurrency(displayedStats.pending)}</span>
          </div>
          <div className="fee-summary-item">
            <span className="fee-label">
              {selectedMonth === 'ALL' ? 'Collection Rate' : `${monthLabel} Rate`}
            </span>
            <span className="fee-amount rate">{displayedStats.rate}%</span>
          </div>
        </div>
      )}
      <button className="fee-view-details" onClick={onViewReports}>
        View Reports
      </button>
    </div>
  );
};

const formatLocalDate = (dateVal) => {
  if (!dateVal) return '';
  const pad = (num) => num < 10 ? `0${num}` : `${num}`;
  if (Array.isArray(dateVal)) {
    const [year, month, day] = dateVal;
    return `${year}-${pad(month)}-${pad(day)}`;
  }
  if (typeof dateVal === 'object') {
    const year = dateVal.year;
    const month = dateVal.monthValue || dateVal.month;
    const day = dateVal.dayOfMonth || dateVal.day;
    if (year && month && day) {
      let m = month;
      if (typeof month === 'string') {
        const months = {
          JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6,
          JULY: 7, AUGUST: 8, SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12
        };
        m = months[month.toUpperCase()] || 1;
      }
      return `${year}-${pad(m)}-${pad(day)}`;
    }
  }
  if (typeof dateVal === 'string') {
    return dateVal.split('T')[0];
  }
  return dateVal.toString();
};

export const AdminAttendanceCard = ({ orgId, axios, token, todaySummary, onNavigate }) => {
  const [activeTab, setActiveTab] = React.useState('today'); // 'today' | 'trend'
  const [activeFilter, setActiveFilter] = React.useState('STUDENT'); // 'ALL' | 'STUDENT' | 'FACULTY'
  const [trendData, setTrendData] = React.useState([]);
  const [trendLoading, setTrendLoading] = React.useState(false);
  const [cachedTrendResponses, setCachedTrendResponses] = React.useState(null);

  // Class filtering states
  const [classesList, setClassesList] = React.useState([]);
  const [selectedClassId, setSelectedClassId] = React.useState('ALL');
  const [classAttendanceData, setClassAttendanceData] = React.useState(null);
  const [classLoading, setClassLoading] = React.useState(false);

  // Fetch all classes metadata
  React.useEffect(() => {
    if (orgId && axios) {
      axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
        headers: {
          accept: 'application/json',
          Authorization: token ? (token.startsWith('Bearer ') ? token : `Bearer ${token}`) : undefined
        }
      })
      .then(res => {
        const list = res.data || [];
        const formattedList = list.map(c => ({
          id: c.classId || c.id,
          name: `${c.className} ${c.sectionName || ''}`.trim()
        })).sort((a, b) => a.name.localeCompare(b.name));
        setClassesList(formattedList);
        if (formattedList.length > 0) {
          setSelectedClassId(formattedList[0].id);
        }
      })
      .catch(err => console.error("Error fetching classes list:", err));
    }
  }, [orgId, axios, token]);

  // Reset class filter if filter changes
  React.useEffect(() => {
    if (activeFilter !== 'STUDENT') {
      setSelectedClassId('ALL');
    } else if (classesList.length > 0) {
      setSelectedClassId(classesList[0].id);
    }
  }, [activeFilter, classesList]);

  // Fetch class attendance for 7 days
  const fetchClassAttendance = React.useCallback(async () => {
    if (!orgId || !axios || selectedClassId === 'ALL') {
      setClassAttendanceData(null);
      return;
    }
    setClassLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      const startDate = sevenDaysAgo.toISOString().split('T')[0];

      const url = configData.getAttendanceRecords('class', selectedClassId);
      let allRecords = [];
      let lastEvaluatedKey = null;

      do {
        const res = await axios.get(url, {
          params: {
            startDate,
            endDate: today,
            pageSize: 100,
            lastEvaluatedKey: lastEvaluatedKey || undefined
          },
          headers: {
            accept: 'application/json',
            Authorization: token ? (token.startsWith('Bearer ') ? token : `Bearer ${token}`) : undefined
          }
        });

        const items = res.data?.items || res.data || [];
        if (Array.isArray(res.data)) {
          allRecords = res.data;
          break;
        }
        allRecords = [...allRecords, ...items];
        lastEvaluatedKey = res.data?.lastEvaluatedKey || null;
      } while (lastEvaluatedKey && lastEvaluatedKey !== '0');

      setClassAttendanceData(allRecords);
    } catch (err) {
      console.error("Error fetching class attendance:", err);
      setClassAttendanceData([]);
    } finally {
      setClassLoading(false);
    }
  }, [orgId, axios, selectedClassId, token]);

  React.useEffect(() => {
    if (activeFilter === 'STUDENT' && selectedClassId !== 'ALL') {
      fetchClassAttendance();
    }
  }, [activeFilter, selectedClassId, fetchClassAttendance]);

  const getCountsForRole = React.useCallback((roleFilter) => {
    const defaultCounts = { PRESENT: 0, ABSENT: 0, EMERGENCY: 0, LATE: 0 };
    
    // Class-specific student stats
    if (roleFilter === 'STUDENT' && selectedClassId !== 'ALL') {
      if (!classAttendanceData) return defaultCounts;
      const today = new Date().toISOString().split('T')[0];
      const todayRecords = classAttendanceData.filter(r => {
        const rDate = formatLocalDate(r.attendanceDate);
        return rDate === today;
      });

      const counts = { ...defaultCounts };
      todayRecords.forEach(r => {
        const status = (r.attendanceStatus || '').toUpperCase();
        if (status === 'PRESENT') counts.PRESENT++;
        else if (status === 'ABSENT') counts.ABSENT++;
        else if (status === 'LATE') counts.LATE++;
        else if (status === 'ON_LEAVE' || status === 'EMERGENCY' || status === 'ONLEAVE') counts.EMERGENCY++;
      });
      return counts;
    }

    // Org-wide stats
    if (!todaySummary) return defaultCounts;

    if (roleFilter === 'ALL') {
      const t = todaySummary.totalByStatus || {};
      return {
        PRESENT: t.PRESENT ?? t.present ?? 0,
        ABSENT: t.ABSENT ?? t.absent ?? 0,
        EMERGENCY: t.EMERGENCY ?? t.emergency ?? 0,
        LATE: t.LATE ?? t.late ?? 0,
      };
    }

    const breakdown = todaySummary.breakdown || {};
    const counts = { ...defaultCounts };

    Object.keys(breakdown).forEach((status) => {
      const statusUpper = status.toUpperCase();
      if (counts[statusUpper] !== undefined) {
        const roleData = breakdown[status] || {};
        if (roleFilter === 'STUDENT') {
          counts[statusUpper] = roleData.STUDENT || roleData.student || 0;
        } else if (roleFilter === 'FACULTY') {
          counts[statusUpper] = (roleData.FACULTY || roleData.faculty || 0) + (roleData.STAFF || roleData.staff || 0);
        }
      }
    });

    return counts;
  }, [todaySummary, selectedClassId, classAttendanceData]);

  const fetchTrendData = React.useCallback(async () => {
    if (!orgId || !axios) return;
    setTrendLoading(true);
    try {
      let responses = cachedTrendResponses;
      const dates = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        dates.push(d.toISOString().split('T')[0]);
      }

      if (!responses) {
        const requests = dates.map((date) =>
          axios.get(configData.get_resources_resources_dashboard_attendance_summary_attendancedate(orgId, date), {
            headers: {
              accept: 'application/json',
              Authorization: token ? (token.startsWith('Bearer ') ? token : `Bearer ${token}`) : undefined
            }
          }).catch((err) => {
            console.warn(`Error fetching trend for date ${date}:`, err);
            return { data: { totalByStatus: {}, date } };
          })
        );
        responses = await Promise.all(requests);
        setCachedTrendResponses(responses);
      }

      const formatted = responses.map((res, index) => {
        const dateStr = dates[index];
        const data = res.data || {};
        const breakdown = data.breakdown || {};
        const totalByStatus = data.totalByStatus || {};

        let present = 0, absent = 0, emergency = 0, late = 0;

        if (activeFilter === 'ALL') {
          present = totalByStatus.PRESENT ?? totalByStatus.present ?? 0;
          absent = totalByStatus.ABSENT ?? totalByStatus.absent ?? 0;
          emergency = totalByStatus.EMERGENCY ?? totalByStatus.emergency ?? 0;
          late = totalByStatus.LATE ?? totalByStatus.late ?? 0;
        } else {
          Object.keys(breakdown).forEach((status) => {
            const roleData = breakdown[status] || {};
            let val = 0;
            if (activeFilter === 'STUDENT') {
              val = roleData.STUDENT || roleData.student || 0;
            } else if (activeFilter === 'FACULTY') {
              val = (roleData.FACULTY || roleData.faculty || 0) + (roleData.STAFF || roleData.staff || 0);
            }
            
            const sUpper = status.toUpperCase();
            if (sUpper === 'PRESENT') present = val;
            else if (sUpper === 'ABSENT') absent = val;
            else if (sUpper === 'EMERGENCY') emergency = val;
            else if (sUpper === 'LATE') late = val;
          });
        }

        const dayTotal = present + absent + emergency + late;
        const rate = dayTotal > 0 ? parseFloat(((present / dayTotal) * 100).toFixed(1)) : 0;
        const dayLabel = new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short' });

        return {
          date: dateStr,
          day: dayLabel,
          rate,
          present,
          absent,
          total: dayTotal
        };
      });

      setTrendData(formatted);
    } catch (err) {
      console.error('Failed to load historical attendance trend:', err);
    } finally {
      setTrendLoading(false);
    }
  }, [orgId, axios, cachedTrendResponses, activeFilter, token]);

  const getTrendDataForClass = React.useCallback(() => {
    if (!classAttendanceData) return [];
    
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(d.toISOString().split('T')[0]);
    }

    return dates.map(dateStr => {
      const dayRecords = classAttendanceData.filter(r => {
        const rDate = formatLocalDate(r.attendanceDate);
        return rDate === dateStr;
      });

      let present = 0, absent = 0, emergency = 0, late = 0;
      dayRecords.forEach(r => {
        const status = (r.attendanceStatus || '').toUpperCase();
        if (status === 'PRESENT') present++;
        else if (status === 'ABSENT') absent++;
        else if (status === 'LATE') late++;
        else if (status === 'ON_LEAVE' || status === 'EMERGENCY' || status === 'ONLEAVE') emergency++;
      });

      const dayTotal = present + absent + emergency + late;
      const rate = dayTotal > 0 ? parseFloat(((present / dayTotal) * 100).toFixed(1)) : 0;
      const dayLabel = new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short' });

      return {
        date: dateStr,
        day: dayLabel,
        rate,
        present,
        absent,
        total: dayTotal
      };
    });
  }, [classAttendanceData]);

  React.useEffect(() => {
    if (activeTab === 'trend' && selectedClassId === 'ALL') {
      fetchTrendData();
    }
  }, [activeTab, activeFilter, orgId, fetchTrendData, selectedClassId]);

  // Compute counts for today
  const counts = getCountsForRole(activeFilter);
  const total = counts.PRESENT + counts.ABSENT + counts.EMERGENCY + counts.LATE;

  const chartData = total > 0 ? [
    { name: 'Present', value: counts.PRESENT, color: '#10b981' },
    { name: 'Absent', value: counts.ABSENT, color: '#ef4444' },
    { name: 'Emergency', value: counts.EMERGENCY, color: '#8b5cf6' },
    { name: 'Late', value: counts.LATE, color: '#f59e0b' },
  ] : [
    { name: 'No data', value: 1, color: '#f1f5f9' }
  ];

  const presentPercentage = total > 0 ? ((counts.PRESENT / total) * 100).toFixed(1) : '0.0';

  const handleRefresh = () => {
    setCachedTrendResponses(null);
    if (activeFilter === 'STUDENT' && selectedClassId !== 'ALL') {
      fetchClassAttendance();
    } else if (activeTab === 'trend') {
      setTimeout(() => fetchTrendData(), 100);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="attendance-tooltip-container" style={{ padding: '8px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: '0.8rem', color: '#1e293b' }}>
            {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </p>
          <div style={{ height: '1px', background: '#f1f5f9', margin: '4px 0' }} />
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#10b981', fontWeight: 600 }}>
            Present Rate: {data.rate}%
          </p>
          <p style={{ margin: 0, fontSize: '0.75rem', color: '#64748b' }}>
            Count: {data.present} of {data.total}
          </p>
        </div>
      );
    }
    return null;
  };

  const displayTrendData = (activeFilter === 'STUDENT' && selectedClassId !== 'ALL')
    ? getTrendDataForClass()
    : trendData;

  const displayTrendLoading = (activeFilter === 'STUDENT' && selectedClassId !== 'ALL')
    ? classLoading
    : trendLoading;

  return (
    <div className="dashboard-card attendance-panel premium-attendance-card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="card-header" style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            Attendance
          </h3>
        </div>
        <div className="attendance-header-controls" style={{ display: 'flex', gap: '6px' }}>
          <select 
            value={activeFilter} 
            onChange={(e) => setActiveFilter(e.target.value)}
            style={{
              fontSize: '0.8rem',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '4px 8px',
              background: '#f8fafc',
              fontWeight: 600,
              color: '#334155',
              cursor: 'pointer'
            }}
          >
            <option value="STUDENT">Students</option>
            <option value="FACULTY">Faculty & Staff</option>
          </select>

          {activeFilter === 'STUDENT' && classesList.length > 0 && (
            <select
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              style={{
                fontSize: '0.8rem',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                padding: '4px 8px',
                background: '#f8fafc',
                fontWeight: 600,
                color: '#334155',
                cursor: 'pointer',
                maxWidth: '120px'
              }}
            >
              <option value="ALL">All Classes</option>
              {classesList.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', marginBottom: '8px' }}>
        <button
          className={`calendar-tab ${activeTab === 'today' ? 'active' : ''}`}
          onClick={() => setActiveTab('today')}
          style={{ padding: '6px 12px', fontSize: '0.75rem', fontWeight: 600, borderRadius: '20px' }}
        >
          Today
        </button>
        <button
          className={`calendar-tab ${activeTab === 'trend' ? 'active' : ''}`}
          onClick={() => setActiveTab('trend')}
          style={{ padding: '6px 12px', fontSize: '0.75rem', fontWeight: 600, borderRadius: '20px' }}
        >
          Weekly Trend
        </button>
        <button
          onClick={handleRefresh}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: 'none',
            color: '#64748b',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            transition: 'background 0.2s'
          }}
          className="refresh-btn"
          title="Refresh statistics"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '220px', justifyContent: 'center' }}>
        {activeTab === 'today' ? (
          (activeFilter === 'STUDENT' && selectedClassId !== 'ALL' && classLoading) ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', flex: 1 }}>
              <CircularProgress size={24} style={{ color: '#3b82f6' }} />
              <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>Loading class details…</span>
            </div>
          ) : total === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: '1.75rem', marginBottom: '8px' }}>📅</div>
              <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', margin: '0 0 4px 0' }}>No attendance marked today</p>
              <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 16px 0', maxWidth: '200px' }}>Start marking attendance to view live analytics.</p>
              <button 
                onClick={() => onNavigate('/attendance/mark')}
                style={{
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
              >
                Mark Attendance
              </button>
            </div>
          ) : (
            <>
              <div className="attendance-chart-container" style={{ minHeight: '160px', margin: '8px 0' }}>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie 
                      data={chartData} 
                      cx="50%" 
                      cy="50%" 
                      innerRadius={45} 
                      outerRadius={65} 
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {chartData.map((entry, idx) => (
                        <Cell key={`cell-pie-${idx}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="attendance-center-text" style={{ pointerEvents: 'none' }}>
                  <div className="attendance-center-number" style={{ fontSize: '1.25rem', color: '#10b981', fontWeight: 800 }}>
                    {presentPercentage}%
                  </div>
                  <div className="attendance-center-label" style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Present
                  </div>
                </div>
              </div>
              <div className="attendance-stats-grid" style={{ marginTop: '8px', gap: '8px' }}>
                <div className="attendance-stat" style={{ background: '#f8fafc', padding: '6px 4px', borderRadius: '6px' }}>
                  <h4 style={{ color: '#10b981', fontSize: '1rem' }}>{counts.PRESENT}</h4>
                  <p style={{ fontSize: '0.65rem' }}>Present</p>
                </div>
                <div className="attendance-stat" style={{ background: '#f8fafc', padding: '6px 4px', borderRadius: '6px' }}>
                  <h4 style={{ color: '#ef4444', fontSize: '1rem' }}>{counts.ABSENT}</h4>
                  <p style={{ fontSize: '0.65rem' }}>Absent</p>
                </div>
                <div className="attendance-stat" style={{ background: '#f8fafc', padding: '6px 4px', borderRadius: '6px' }}>
                  <h4 style={{ color: '#f59e0b', fontSize: '1rem' }}>{counts.LATE}</h4>
                  <p style={{ fontSize: '0.65rem' }}>Late</p>
                </div>
              </div>
            </>
          )
        ) : (
          displayTrendLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', flex: 1 }}>
              <CircularProgress size={24} style={{ color: '#3b82f6' }} />
              <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>Fetching weekly trends…</span>
            </div>
          ) : displayTrendData.length === 0 || displayTrendData.every(d => d.total === 0) ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: '1.75rem', marginBottom: '8px' }}>📈</div>
              <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', margin: '0' }}>No historical trend data</p>
              <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '4px 0 0 0' }}>Trend metrics will populate as attendance is captured.</p>
            </div>
          ) : (
            <div style={{ width: '100%', height: 160, display: 'flex', alignItems: 'center', marginTop: '12px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={displayTrendData} margin={{ top: 5, right: 5, left: -25, bottom: 5 }}>
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#64748b', fontWeight: 600 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(241, 245, 249, 0.5)' }} />
                  <Bar dataKey="rate" radius={[4, 4, 0, 0]} maxBarSize={20}>
                    {displayTrendData.map((entry, index) => {
                      const colors = ['#3b82f6', '#10b981', '#6366f1', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6'];
                      return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f1f5f9', marginTop: '16px', paddingTop: '8px' }}>
        <button 
          onClick={() => onNavigate('/attendance/mark')}
          style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          + Mark Attendance
        </button>
      </div>
    </div>
  );
};

export const AdminCommunicationCard = ({ isLoadingMessages, messages, onOpenInbox }) => (
  <div className="dashboard-card communication-panel">
    <div className="card-header">
      <h3 className="card-title">Communication</h3>
      <button type="button" className="view-all-link" onClick={onOpenInbox}>View All</button>
    </div>
    <div className="messages-list">
      {isLoadingMessages ? (
        <div style={{ padding: '20px', textAlign: 'center', opacity: 0.6 }}>Loading messages...</div>
      ) : messages.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No messages" description="Your inbox is currently clear." containerHeight="160px" />
      ) : (
        messages.map((message, index) => (
          <div key={index} className="message-item">
            <div className={`message-avatar ${message.className}`}>{message.avatar}</div>
            <div className="message-content">
              <h4 className="message-name">{message.name}</h4>
              <p className="message-text">{message.message}</p>
              <span className="message-time">{message.time}</span>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);
